import { Controller } from "@hotwired/stimulus"

const DIGIT_MS = 1000
const BLANK_MS = 500
const FEEDBACK_MS = 900

export default class extends Controller {
  static values = {
    startingLength: { type: Number, default: 3 },
    maxLength: { type: Number, default: 12 },
    trialsPerLength: { type: Number, default: 2 },
    formId: String
  }

  connect() {
    this.state = {
      length: this.startingLengthValue,
      trialInLen: 0,
      passCount: 0,
      longestPassed: 0,
      history: []
    }
    this.overlay = document.createElement("div")
    this.overlay.className = "ds-overlay"

    // Exit button
    const exit = document.createElement("button")
    exit.className = "ds-exit"
    exit.innerHTML = "&#x2715;"
    exit.addEventListener("click", () => this.finish({ aborted: true }))
    this.overlay.appendChild(exit)

    this.stage = document.createElement("div")
    this.stage.className = "ds-stage"
    this.overlay.appendChild(this.stage)
    document.body.appendChild(this.overlay)

    // Opening animation
    requestAnimationFrame(() => this.overlay.classList.add("ds-open"))
    this.showSplash()
  }

  disconnect() {
    this.overlay?.remove()
  }

  showSplash() {
    this.stage.innerHTML = `
      <div>
        <div class="ds-splash-hint">Digits will appear one at a time.<br>Type them back in the same order.</div>
        <button class="ds-start" id="ds-start">Start</button>
      </div>
    `
    document.getElementById("ds-start").addEventListener("click", () => this.start())
  }

  start() {
    this.currentSequence = this.generateSequence(this.state.length)
    this.showSequence(this.currentSequence, 0)
  }

  generateSequence(length) {
    let s = ""
    for (let i = 0; i < length; i++) s += Math.floor(Math.random() * 10)
    return s
  }

  showSequence(seq, i) {
    if (i >= seq.length) {
      this.stage.innerHTML = ""
      setTimeout(() => this.promptInput(seq), BLANK_MS)
      return
    }
    this.stage.innerHTML = ""
    setTimeout(() => {
      this.stage.innerHTML = `<div class="ds-digit">${seq[i]}</div>`
      setTimeout(() => this.showSequence(seq, i + 1), DIGIT_MS)
    }, BLANK_MS)
  }

  promptInput(seq) {
    const trialNum = this.state.trialInLen + 1
    this.stage.innerHTML = `
      <div>
        <div class="ds-prompt">Type the sequence</div>
        <input type="text" class="ds-input" id="ds-answer" inputmode="numeric" autocomplete="off" autocapitalize="off" spellcheck="false" />
        <button class="ds-submit" id="ds-submit">&#x2192;</button>
      </div>
    `
    const input = document.getElementById("ds-answer")
    input.focus()

    let submitted = false
    const submit = () => {
      if (submitted) return
      submitted = true
      this.evaluate(seq, input.value.replace(/\D/g, ""))
    }

    input.addEventListener("input", (e) => {
      const clean = e.target.value.replace(/\D/g, "")
      if (clean !== e.target.value) e.target.value = clean
    })
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") submit()
    })

    document.getElementById("ds-submit").addEventListener("click", submit)
  }

  evaluate(shown, answer) {
    const pass = answer === shown
    this.state.history.push({ length: this.state.length, sequence: shown, answer, passed: pass })

    this.stage.innerHTML = `<div class="ds-feedback ${pass ? "ds-pass" : "ds-fail"}">${pass ? "✓" : "✗"}</div>`

    if (pass) this.state.passCount += 1

    setTimeout(() => this.advance(), FEEDBACK_MS)
  }

  advance() {
    this.state.trialInLen += 1

    if (this.state.trialInLen < this.trialsPerLengthValue) {
      this.start()
      return
    }

    const allPassed = this.state.passCount === this.trialsPerLengthValue

    if (allPassed) {
      this.state.longestPassed = this.state.length
      if (this.state.length >= this.maxLengthValue) {
        this.finish()
        return
      }
      this.state.length += 1
      this.state.trialInLen = 0
      this.state.passCount = 0
      this.start()
    } else {
      this.finish()
    }
  }

  finish({ aborted = false } = {}) {
    let score = this.state.longestPassed
    const nextTrials = this.state.history.filter(t => t.length === this.state.longestPassed + 1)
    if (nextTrials.some(t => t.passed)) score += 0.5

    // Populate form and submit
    const form = document.getElementById(this.formIdValue)
    form.querySelector('[data-tasks--digit-span-target="scoreInput"]').value = score
    form.querySelector('[data-tasks--digit-span-target="trialsInput"]').value = JSON.stringify(this.state.history)
    form.querySelector('[data-tasks--digit-span-target="abortedInput"]').value = aborted

    // Closing animation then submit
    this.overlay.classList.remove("ds-open")
    this.overlay.addEventListener("transitionend", () => {
      this.overlay.remove()
      form.requestSubmit()
    }, { once: true })
  }
}
